import videojs from 'video.js';
import Fn from 'video.js';

import { v4 as uuid } from 'uuid';

import SeekBarSegment from './seekbar-segments/seekbar-segment.js';

const _seekBarSegments = new WeakMap();

/**
 * @typedef {Object} seekBarSegmentConfig
 * @alias SeekBarSegmentsConfig
 * @memberof SeekBarSegmentsPlugin
 * @property {string} [id] An ID by which to identify the segment.
 * @property {number} time The time in seconds.
 * @property {string} label A label that will be visible in the UI.
 * @property {number} [spacerWidth] The segment width in pixels.
 * @property {boolean} [progress] Whether a PlayProgressBar should be added to the segment
 */

/**
 * The SeekBarSegments module.
 * @member {SeekBarSegmentsPlugin} seekBarSegments
 * @memberof MediasitePlayer#
 */

/**
 * Configures segments to break up seekbar into sections.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class SeekBarSegmentsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);
        _seekBarSegments.set(this, {});
    }
    /**
     * 
     * @param {SeekBarSegmentsConfig} segmentConfig A segment to add to the timeline.
     * @returns {string} The segment's ID.
     * @example
     * const segmentId = player.seekBarSegments.add({
     *     startTime: 0.0,
     *     endTime: 20.0,
     *     label: 'Introduction',
     *     spacerWidth: 5,
     *     progress: true
     * });
     */
    add(segmentConfig) {
        const { startTime, endTime, label, spacerWidth, progress } = segmentConfig;
        const id = segmentConfig.id ?? uuid();
        const seekBarSegment = new SeekBarSegment(this.player, { startTime, endTime, label, spacerWidth, progress });
        const seekBar = this.player.getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar');
        seekBar.addChild(seekBarSegment);
        _seekBarSegments.get(this)[id] = seekBarSegment;

        this.#sortAll();

        return id;
    }

    /**
     * Removes a segment from the seekbar.
     * @param {string} id The segment's unique ID.
     * @example
     * player.seekBarSegments.remove('segmentId');
     */
    remove(id) {
        const segment = _seekBarSegments.get(this)[id];
        if (segment) {
            const controlBar = this.player.getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar');
            controlBar.removeChild(segment);
            delete _seekBarSegments.get(this)[id];
            this.#sortAll();
        }
    }

    /**
     * Updates the timeline segment with the given ID.
     * @param {string} id
     * @param {Object} data
     * @param {number} data.startTime The start time in seconds.
     * @param {number} data.endTime The end time in seconds.
     * @param {string} data.label A label for the segment.
     * @param {number} data.spacerWidth The width of a segment.
     * @param {boolean} data.progress Whether a segment includes a progress bar.
     * @example
     * player.seekBarSegments.update('segmentId', { });
     */
    update(id, data) {
        const { startTime, endTime, label, spacerWidth, progress } = (data ?? {});

        const segment = _seekBarSegments.get(this)[id];
        if (segment) {
            // Update timeline segment
            if (startTime) {
                segment.startTime = startTime;
            }
            if (endTime) {
                segment.endTime = endTime;
            }
            if (label) {
                segment.label = label;
            }
            if (spacerWidth) {
                segment.spacerWidth = spacerWidth;
            }
            if (progress) {
                segment.progress = progress;
            }
            segment.hidden = !data;
        }

        this.#sortAll();
    }

    /**
     * Gets the data for the timeline segment with the given ID.
     * @param {string} id
     * @returns {{text:string,time:number,spacerWidth:number}}
     * @example
     * const segment = player.seekBarSegments.get('segmentId');
     */
    get(id) {
        const segment = _seekBarSegments.get(this)[id];
        if (segment) {
            return {
                text: segment.label,
                time: segment.time,
                spacerWidth: segment.spacerWidth
            }
        }
    }



    #sortAll() {
        const seekBarSegments = Object.values(_seekBarSegments.get(this)).sort((a, b) => {
            if (a.startTime > b.endTime) {
                return 1;
            }
            else if (a.startTime < b.endTime) {
                return 0;
            }
            return 0;
        });

        const playProgressBar = this.player.getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar').getChild('PlayProgressBar');
        for (const seekBarSegment of seekBarSegments) {
            playProgressBar.addChild(seekBarSegment);
        }
    }

    dispose() {
        const seekBarSegments = _seekBarSegments.get(this);
        for (const segmentId of Object.keys(seekBarSegments)) {
            this.remove(segmentId);
        }

        super.dispose();
    }
}

export default SeekBarSegmentsPlugin;
